<template>
  <div class="range-slider-container">
    <input
      ref="rangeInput"
      type="range"
      :min="min"
      :max="max"
      :step="step"
      :value="currentValue"
      :style="{ '--value-percent': `${valuePercent}%` }"
      class="range-slider"
      @input="updateValue"
    />
    <div
      v-if="showLabel"
      ref="label"
      class="input-range-label text-center text-md font-medium"
      :style="{ left: `${labelOffset}px` }"
    >
      {{ currentValue }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'RangeSlider',
  props: {
    // Vue 3 style (primary)
    modelValue: {
      type: [Number, String],
      default: undefined,
    },
    // Vue 2 style (fallback for backward compatibility)
    value: {
      type: [Number, String],
      default: undefined,
    },
    min: {
      type: [Number, String],
      default: 0,
    },
    max: {
      type: [Number, String],
      default: 100,
    },
    step: {
      type: [Number, String],
      default: 1,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['input', 'update:modelValue'],
  data() {
    return {
      labelOffset: 0,
      labelWidth: 0,
    };
  },
  computed: {
    // Use modelValue if provided, otherwise fall back to value
    currentValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    valuePercent() {
      return ((this.currentValue - this.min) / (this.max - this.min)) * 100;
    },
  },
  watch: {
    currentValue: {
      handler() {
        this.$nextTick(this.calculateLabelOffset);
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateLabelOffset();
    });
  },
  methods: {
    updateValue(event) {
      const newValue = Number(event.target.value);
      // Emit both events for backward compatibility
      this.$emit('input', newValue);
      this.$emit('update:modelValue', newValue);
    },
    calculateLabelOffset() {
      if (this.$refs.label) {
        this.labelWidth = this.$refs.label.offsetWidth;
        const thumbCenter = this.thumbCenter(this.$refs.rangeInput.offsetWidth, this.valuePercent);
        this.labelOffset = thumbCenter - (this.labelWidth / 2);
      }
    },
    thumbCenter(rangeWidth, valuePercent) {
      const thumbWidth = 20;
      return (valuePercent / 100) * (rangeWidth - thumbWidth) + thumbWidth / 2;
    },
  },
};
</script>

<style scoped lang="scss">
.range-slider-container {
  width: 100%;
  padding: 10px 0;
  position: relative;
  min-height: rem-calc(48);
}

.range-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 8px;
  background: var(--bg-quaternary);
  border-radius: 4px;
  outline: none;
}

.range-slider::-webkit-slider-runnable-track {
  height: 8px;
  border-radius: 4px;
  background: linear-gradient(
    to right,
    var(--fg-brand-primary-600) var(--value-percent, 0%),
    var(--bg-quaternary) var(--value-percent, 0%)
  );
}

.range-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: white;
  border: 2px solid var(--fg-brand-primary-600);
  border-radius: 50%;
  cursor: pointer;
  margin-top: -6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.range-slider::-moz-range-track {
  height: 8px;
  border-radius: 4px;
  background: var(--bg-quaternary);
}

.range-slider::-moz-range-progress {
  height: 8px;
  border-radius: 4px;
  background: var(--fg-brand-primary-600);
}

.range-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: white;
  border: 2px solid var(--fg-brand-primary-600);
  border-radius: 50%;
  cursor: pointer;
}

.input-range-label {
  margin-top: 10px;
  position: absolute;
}

</style>
